/**
 * ContractObligationsTable - the cross-contract obligations list on Contracts
 * (R4-06 / R4-07).
 *
 * It was a hand-rolled list whose rows only navigated to the contract, and
 * nothing could be done to an obligation from here. Now it is the shared
 * Table, and every row can be acted on where it stands:
 *
 *   - click → the contract's Obligations tab, scrolled to and highlighting
 *     THIS obligation (`?obligation=<uuid>`),
 *   - Create task (assign it and track it to done) / Open task,
 *   - Mark done, Waive - the obligation's own statuses (open/done/waived);
 *     the feed lists open ones only, so a closed row leaves the list,
 *   - Open contract.
 *
 * The backend already has every one of these (PATCH status, promote to task).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  PiArrowSquareOutLight, PiCalendarPlusLight, PiCheckCircleLight, PiListChecksLight,
  PiProhibitLight, PiScrollLight,
} from 'react-icons/pi';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { useAppDispatch } from '../../store';
import {
  ObligationFeedItem, createObligationTask, updateObligation,
} from '../../utils/contractsApi';

/** Where a row click lands: the contract's Obligations tab, on this obligation. */
export const obligationHref = (o: Pick<ObligationFeedItem, 'contract_uuid' | 'uuid'>) =>
  `/contracts/${o.contract_uuid}/obligations?obligation=${encodeURIComponent(o.uuid)}`;

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

interface Props {
  obligations: ObligationFeedItem[];
  loading?: boolean;
  /** The list after an action - a closed obligation removed, a promoted one updated. */
  onChange: (next: ObligationFeedItem[]) => void;
}

const ContractObligationsTable: React.FC<Props> = ({ obligations, loading, onChange }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState<string | null>(null);

  const cObligation = t('Obligation');
  const cContract = t('Contract');
  const cDue = t('Due');
  const cStatus = t('Status');

  const notify = (modalType: 'success' | 'error', message: string) =>
    dispatch(showFeedbackModal({ showModal: true, modalType, message, duration: 3500 }));

  const byId = (id: string) => obligations.find((o) => o.uuid === id);

  const close = async (o: ObligationFeedItem, status: 'done' | 'waived') => {
    setBusy(o.uuid);
    try {
      await updateObligation(o.contract_uuid, o.uuid, { status });
      onChange(obligations.filter((x) => x.uuid !== o.uuid));
      notify('success', status === 'done' ? t('Obligation marked done') : t('Obligation waived'));
    } catch {
      notify('error', t('Could not update the obligation.'));
    } finally {
      setBusy(null);
    }
  };

  const promote = async (o: ObligationFeedItem) => {
    setBusy(o.uuid);
    try {
      const updated = await createObligationTask(o.contract_uuid, o.uuid);
      onChange(obligations.map((x) => (x.uuid === o.uuid ? { ...x, ...updated } : x)));
      notify('success', t('Task created'));
    } catch {
      notify('error', t('Could not create the task.'));
    } finally {
      setBusy(null);
    }
  };

  if (!loading && obligations.length === 0) {
    return (
      <EmptyState density="compact" icon={<PiListChecksLight />}
        headline={t('No obligations due')}
        subtext={t('Open obligations with a due date show up here, overdue first.')} />
    );
  }

  return (
    <Table
      tableId="contracts-obligations"
      isLoading={loading}
      columns={[cObligation, cContract, cDue, cStatus, 'Actions']}
      data={obligations.map((o) => ({
        id: o.uuid,
        [cObligation]: o.description,
        [cContract]: o.contract_title,
        [cDue]: o.due_date || '',
        [cStatus]: o.days_to_due ?? Number.MAX_SAFE_INTEGER,
      }))}
      clientSort
      sortableColumns={[cObligation, cContract, cDue]}
      sortAccessors={{ [cDue]: (row) => (row[cDue] ? new Date(row[cDue]).getTime() : Number.MAX_SAFE_INTEGER) }}
      defaultColumnWidths={{ [cObligation]: 360, [cContract]: 240, [cDue]: 140, [cStatus]: 180, Actions: 56 }}
      onRowClick={(row) => { const o = byId(row.id); if (o) history.push(obligationHref(o)); }}
      renderCell={(col, value, row) => {
        const o = byId(row.id);
        if (!o) return value;
        if (col === cObligation) return <span className="text-fg-strong" title={o.description}>{o.description}</span>;
        if (col === cContract) return <span className="text-fg-muted" title={o.contract_title}>{o.contract_title}</span>;
        if (col === cDue) return <span className="text-fg-body">{fmtDate(o.due_date)}</span>;
        if (col === cStatus) {
          const overdue = o.days_to_due != null && o.days_to_due < 0;
          const soon = o.days_to_due != null && o.days_to_due >= 0 && o.days_to_due <= 7;
          return (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <Badge color={overdue ? 'red' : soon ? 'yellow' : 'gray'}>
                {overdue ? t('Overdue') : o.days_to_due != null ? t('{{n}}d', { n: o.days_to_due }) : '—'}
              </Badge>
              {o.tracked && <Badge color="blue">{t('Task')}</Badge>}
            </span>
          );
        }
        return value;
      }}
      actions={[
        {
          label: t('Create task'),
          icon: <PiCalendarPlusLight className="h-4 w-4" />,
          hidden: (row) => !!byId(row.id)?.tracked,
          disabled: (row) => busy === row.id,
          onClick: (row) => { const o = byId(row.id); if (o) promote(o); },
        },
        {
          label: t('Open task'),
          icon: <PiArrowSquareOutLight className="h-4 w-4" />,
          hidden: (row) => !byId(row.id)?.task_uuid,
          onClick: (row) => { const o = byId(row.id); if (o?.task_uuid) history.push(`/tasks/${o.task_uuid}`); },
        },
        {
          label: t('Mark done'),
          icon: <PiCheckCircleLight className="h-4 w-4" />,
          disabled: (row) => busy === row.id,
          onClick: (row) => { const o = byId(row.id); if (o) close(o, 'done'); },
        },
        {
          label: t('Waive'),
          icon: <PiProhibitLight className="h-4 w-4" />,
          disabled: (row) => busy === row.id,
          onClick: (row) => { const o = byId(row.id); if (o) close(o, 'waived'); },
        },
        {
          label: t('Open contract'),
          icon: <PiScrollLight className="h-4 w-4" />,
          onClick: (row) => { const o = byId(row.id); if (o) history.push(`/contracts/${o.contract_uuid}`); },
        },
      ]}
    />
  );
};

export default ContractObligationsTable;
